import { Component, Input, Output, EventEmitter, ViewChild, AfterViewInit, OnDestroy, OnInit, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { DropdownModel, FilterModel, SidebarRouteExtended, TogglerBasicModel, TogglerModel } from '@shared/models';
import { NotificationService, SessionStorageService, UserService } from '@shared/services';

@Component({
  selector:      'content-header',
  templateUrl: './content-header.component.html',
  styleUrls:  ['./content-header.component.sass'],
  host: { 'class': 'am-flex-align am-flex-wrap gap-auto content-header-wrapper' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContentHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('toggler') toggler;
  enoughSpace: boolean
  observer:    ResizeObserver;

  @Input() headerTitle: string;
  @Input() activeTab:   SidebarRouteExtended;
  @Input() loading:     boolean;

  @Input() dropdowns:   DropdownModel[];
  @Input() togglers:    TogglerModel[];
  @Input() filters:     FilterModel[];
  @Input() button:      any;

  @Output() togglersChanged  = new EventEmitter<any>();
  @Output() filtersChanged   = new EventEmitter<any>();
  @Output() dropdownsChanged = new EventEmitter<any>();
  @Output() buttonCallback   = new EventEmitter<any>();

  isInternal: boolean;
  isCustomer: boolean;

  @Input() entriesLength:          boolean;
  @Input() enablePrintExtra:       boolean;
  @Input() enableDownloadExtra:    boolean;
  @Input() togglersAllowToDisable: boolean;

  @Input() printAvailable: boolean;
  @Input() excelAvailable: boolean;
  @Input() disablePrint:   boolean;
  @Input() disableExcel:   boolean;
  @Output() print      = new EventEmitter<any>();
  @Output() download   = new EventEmitter<any>();
  @Output() reload     = new EventEmitter<any>();
  @Output() excel      = new EventEmitter<any>();
  @Output() printExtra = new EventEmitter<any>();
  @Output() downloadExtra = new EventEmitter<any>();

  @HostListener('document:click', ['$event'])
  clickout(event) {
    if (this.dropdowns?.length && !event.target.closest('.dropdown-wrapper')) {
      this.dropdowns.forEach(d => d.collapse = null);
    }
  }

  constructor(
    private userService:           UserService,
    private notificationService:   NotificationService,
    public  sessionStorageService: SessionStorageService
  ) { }

  ngOnInit(): void {
    this.userService.currentUser.subscribe(user => {
      this.isCustomer = user.isCustomer;
      this.isInternal = user.isInternal;
    });
  }

  ngAfterViewInit(): void {
    this.checkSpace();
    this.observer = new ResizeObserver(() => this.checkSpace());
    this.togglers && this.observer.observe(this.toggler.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer.disconnect();
  }

  get togglersByUserRole():  TogglerModel[]  { return this.filterByUserRole(this.togglers)  as TogglerModel[];  }
  get filtersByUserRole():   FilterModel[]   { return this.filterByUserRole(this.filters)   as FilterModel[];   }
  get dropdownsByUserRole(): DropdownModel[] { return this.filterByUserRole(this.dropdowns) as DropdownModel[]; }

  private filterByUserRole(items: any): TogglerModel[] | FilterModel[] | DropdownModel[] {
    return items?.filter(tog => !tog.isInternal && !tog.isCustomer || tog.isInternal && this.isInternal || tog.isCustomer && this.isCustomer)
                 .filter(tog => tog.beta ? this.checkBeta(tog.beta) : true);
  }

  private checkBeta(beta: string): boolean {
    let features = this.userService.currentUserValue.features;
    return features && (features[beta] === true || features[beta].length);
  }

  get downloadAvailable(): boolean {
    return !!this.download.observed;
  }

  get reloadAvailable(): boolean {
    return !!this.reload.observed;
  }

  reloadHandler(): void {
    this.notificationService.wait();
    this.reload.emit();
  }

  private checkSpace(): void {
    if (this.toggler) {
      const togglersValues = this.togglersByUserRole.map(t => t.label);
      const container      = this.toggler.nativeElement;
      const containerWidth = container.offsetWidth;

      const font    = getComputedStyle(container).font;
      const canvas  = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      context.font  = font;

      let fullWidth = togglersValues.reduce((sum, val): any => {
        sum  = sum + 8 + 8 + 24 + 8 + context.measureText(val).width;
        return sum;
      }, 0);
      this.enoughSpace = fullWidth <= containerWidth;
    }
  }

  getDropdownLabel(dropdown: DropdownModel): string {
    return dropdown.options.find(o => o.value === dropdown.value).label;
  }

  dropdownSelect(dropdown: DropdownModel, option: TogglerBasicModel): void {
    dropdown.collapse = null;
    this.dropdownsChanged.emit({ field: dropdown.field, value: option.value });
  }

}
